import { FC } from 'react';
import { Navigate } from 'react-router-dom';
import { ViewLoader } from '~/components';
import { withErrorBoundary } from '~/hocs';
import { useParams, useSelector } from '~/hooks';
import { useIsSSO } from '~/hooks/useIsSSO';
import { useOrganizationByShortnameQuery, useWorkspacesByOrganizationShortName } from '~/queries';

/**
 * This is used as placeholder route to redirect user further down the path
 * 1. if user doesn't have any organization workspaces -> send to create new workspace view
 * 2. if user has permissions to view dashboard -> send to dashboard page
 * 3. send to organization settings page
 */
export const OrganizationRoot: FC = withErrorBoundary(() => {
  const { organizationShortName } = useParams<'organizationShortName'>();
  const { isSSO } = useIsSSO();

  const { data: organization, isFetching: isFetchingOrganization } =
    useOrganizationByShortnameQuery({
      organizationShortName,
    });
  const { data: workspaces = [], isFetching: isFetchingWorkspaces } =
    useWorkspacesByOrganizationShortName({
      organizationShortName,
    });

  const userCanManageWorkspaces = useSelector(
    ({ userResources }) => userResources.users.current.limit_overrides.canManageWorkspaces,
  );

  if (isFetchingOrganization || isFetchingWorkspaces) {
    return <ViewLoader key="root" />;
  }

  const canManageWorkspaces =
    userCanManageWorkspaces && organization?.permissions?.includes('manage_workspaces');
  if (!isSSO && workspaces.length === 0 && canManageWorkspaces) {
    return <Navigate to={`/${organizationShortName}/create-workspace`} replace />;
  }

  const canViewDashboard = organization?.permissions?.includes('view_exec_dashboards');
  if (canViewDashboard) {
    return <Navigate to={`/${organizationShortName}/dashboard`} replace />;
  }

  return <Navigate to="/welcome" replace />;
});
